import React, { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ColumnDef, SortingState } from '@tanstack/react-table';
import { getTaxLots, TaxLotItem, TaxLotsParams } from '../../api/tax';
import { useTaxYearStore } from '../../stores/useTaxYearStore';
import { useAutoRefreshTrigger } from '../../stores/useGlobalAutoRefreshStore';
import { formatUsd, formatCrypto, formatDate, formatOrderId } from './taxReportingUtils';
import DataTable, { DataTableFilter } from '../shared/DataTable';
import { usePortfolioRegistry } from '../../hooks/usePortfolioRegistry';

// Server-paginated. See CoinbaseTransactionsView.tsx for the canonical idiom.

type SourceType = 'COINBASE' | 'MANUAL';

const sourceBadge = (v: SourceType) => (
  <span className={v === 'COINBASE' ? 'badge badge-buy' : 'badge badge-sell'}>{v}</span>
);

export const TaxLotsView: React.FC = () => {
  const taxYear = useTaxYearStore((s) => s.selectedYear);
  const refreshTrigger = useAutoRefreshTrigger('tax-reporting');

  const [portfolioId, setPortfolioId] = useState('');
  const [productId, setProductId] = useState('');
  const [sourceType, setSourceType] = useState<'' | SourceType>('');
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(100);
  const [sorting, setSorting] = useState<SortingState>([{ id: 'disposal_date', desc: true }]);
  const sortBy = sorting[0]?.id;
  const sortDir: 'asc' | 'desc' | undefined = sorting[0] ? (sorting[0].desc ? 'desc' : 'asc') : undefined;


  const onFilterChange = (setter: (v: string) => void) => (v: string) => {
    setter(v);
    setPageIndex(0);
  };

  const params: TaxLotsParams = {
    taxYear,
    portfolioId: portfolioId || undefined,
    productId: productId || undefined,
    sourceType: (sourceType || undefined) as SourceType | undefined,
    sortBy,
    sortDir,
    page: pageIndex + 1,
    pageSize,
  };

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['tax', 'tax-lots', taxYear, portfolioId, productId, sourceType, sortBy, sortDir, pageIndex, pageSize, refreshTrigger],
    queryFn: () => getTaxLots(params),
  });

  const items = data?.items ?? [];
  const total = data?.metadata.total ?? 0;
  const pageCount = data ? Math.max(1, Math.ceil(total / pageSize)) : -1;

  const { options: portfolioOptions } = usePortfolioRegistry();
  const productOptions = useMemo(
    () => Array.from(new Set(items.map((i) => i.product_id))).sort().map((p) => ({ value: p, label: p })),
    [items]
  );

  const columns = useMemo<ColumnDef<TaxLotItem>[]>(
    () => [
      { accessorKey: 'lot_id', header: 'Lot ID' },
      { accessorKey: 'portfolio_id', header: 'Portfolio' },
      { accessorKey: 'product_id', header: 'Product' },
      {
        accessorKey: 'acquisition_source_type',
        header: 'Acq Src',
        cell: (info) => sourceBadge(info.getValue() as SourceType),
      },
      {
        accessorKey: 'acquisition_order_id',
        header: 'Acq Order',
        cell: (info) => <span className="order-id-cell">{formatOrderId(info.getValue() as string | null)}</span>,
        enableSorting: false,
      },
      {
        accessorKey: 'acquisition_date',
        header: 'Acq Date',
        cell: (info) => <span className="date-cell">{formatDate(info.getValue() as string)}</span>,
      },
      { accessorKey: 'acquisition_cost', header: 'Acq Cost', cell: (info) => formatUsd(info.getValue() as string) },
      {
        accessorKey: 'disposal_source_type',
        header: 'Disp Src',
        cell: (info) => sourceBadge(info.getValue() as SourceType),
      },
      {
        accessorKey: 'disposal_order_id',
        header: 'Disp Order',
        cell: (info) => <span className="order-id-cell">{formatOrderId(info.getValue() as string | null)}</span>,
        enableSorting: false,
      },
      {
        accessorKey: 'disposal_date',
        header: 'Disp Date',
        cell: (info) => <span className="date-cell">{formatDate(info.getValue() as string)}</span>,
      },
      { accessorKey: 'proceeds', header: 'Proceeds', cell: (info) => formatUsd(info.getValue() as string) },
      // Gain/Loss = proceeds − acquisition_cost − fees_acquisition − fees_disposal.
      // Computed client-side from the API row's string decimals — no backend change needed.
      {
        id: 'gain_loss',
        header: 'Gain/Loss',
        accessorFn: (row) => {
          const proceeds = parseFloat(row.proceeds ?? '0');
          const cost = parseFloat(row.acquisition_cost ?? '0');
          const acqFees = parseFloat(row.fees_acquisition ?? '0');
          const dispFees = parseFloat(row.fees_disposal ?? '0');
          if ([proceeds, cost, acqFees, dispFees].some(Number.isNaN)) return null;
          return proceeds - cost - acqFees - dispFees;
        },
        cell: (info) => {
          const v = info.getValue() as number | null;
          if (v === null) return formatUsd(null);
          const cls = v < 0 ? 'pnl-negative' : v > 0 ? 'pnl-positive' : '';
          return <span className={cls}>{formatUsd(v)}</span>;
        },
        sortingFn: 'basic',
      },
      { accessorKey: 'quantity', header: 'Quantity', cell: (info) => formatCrypto(info.getValue() as string) },
      { accessorKey: 'holding_period', header: 'Holding' },
      { accessorKey: 'lot_source', header: 'Lot Source' },
    ],
    []
  );

  const filters: DataTableFilter[] = [
    { id: 'portfolio_id', label: 'Portfolio', value: portfolioId, options: portfolioOptions, onChange: onFilterChange(setPortfolioId) },
    { id: 'product_id', label: 'Product', value: productId, options: productOptions, onChange: onFilterChange(setProductId) },
    {
      id: 'source_type',
      label: 'Source',
      value: sourceType,
      options: [
        { value: 'COINBASE', label: 'COINBASE' },
        { value: 'MANUAL', label: 'MANUAL' },
      ],
      onChange: (v) => {
        setSourceType(v as '' | SourceType);
        setPageIndex(0);
      },
    },
  ];

  return (
    <div aria-label="Tax Lots view">
      <DataTable<TaxLotItem>
        data={items}
        columns={columns}
        serverSorting={{
          state: sorting,
          onChange: (s) => {
            setSorting(s);
            setPageIndex(0);
          },
        }}
        filters={filters}
        loadingMessage="Loading tax lots…"
        isLoading={isLoading}
        isError={isError}
        errorMessage={error instanceof Error ? error.message : 'Failed to load tax lots'}
        emptyMessage="No tax lots for these filters."
        serverPagination={{
          pageIndex,
          pageSize,
          pageCount,
          totalRows: total,
          onPageIndexChange: setPageIndex,
          onPageSizeChange: (size) => {
            setPageSize(size);
            setPageIndex(0);
          },
        }}
      />
    </div>
  );
};

export default TaxLotsView;
